"use strict";
var base = require("./base");
const constants = require("../helpers/const");
const accessibleDialog = require("../helpers/accessibleDialog");
const { moveToActiveColor, initColorsCarousel } = require("./productColorCarouselHelpers");
const { matchWidth } = require("../components/matchWidth");
const { invalid } = require("../components/clientSideValidation");
const { md } = require("../breakpoints").globalBreakpoints;

const QUICK_VIEW_MODAL_SELECTOR = "#quickViewModal";

// The tile control the drawer was opened from. Focus returns here on close rather than to whatever
// happens to be focused at the time: the drawer's own Add to Bag button is destroyed as the drawer
// closes, and Safari does not focus a <button> when it is clicked, so document.activeElement is not
// a reliable record of the trigger (DGF-3991).
let $quickViewTrigger = null;

// Set for the one close that follows an orientation change, where the drawer is re-opened straight
// away and so has to disappear at once rather than slide out.
let isForcedHide = false;

/**
* Hides the Quick View panel.
*
* Split out from closeQuickViewModal so the accessible dialog helper can drive it: the helper makes
* the page interactive again and returns focus, then calls back here to do the hiding (DGF-3991).
*
* @param {jQuery} $modal - the Quick View modal
* @param {boolean} isForceHideModal - flag to indicate if modal should be immediately hidden without animation
* @returns {void}
*/
function hideQuickViewPanel($modal, isForceHideModal) {
    if (isForceHideModal) {
        $modal.modal("hide");
        $(".modal-backdrop").remove();
        return;
    }

    $modal.removeClass("open");

    // Have a backdrop animation on closing modal as per design requirements
    setTimeout(() => {
        $modal.modal("hide");
    }, 500);
}

/**
* Closes the Quick View modal by removing the 'open' class and hiding the modal after a delay.
* @function
* @param {boolean} isForceHideModal - flag to indicate if modal should be immediately hidden without animation
* @returns {void}
*/
function closeQuickViewModal(isForceHideModal) {
    const $modal = $(QUICK_VIEW_MODAL_SELECTOR);

    isForcedHide = !!isForceHideModal;

    // The helper un-inerts the page and returns focus, then hides the panel through the onClose
    // callback registered in openQuickViewDialog. It returns false when the drawer was not opened
    // through the helper, in which case hide it directly (DGF-3991).
    if (!accessibleDialog.close($modal)) {
        hideQuickViewPanel($modal, isForcedHide);
    }

    isForcedHide = false;
}

/**
* Applies the WAI-ARIA modal dialog pattern to the drawer once it is on screen.
*
* Bootstrap shows the drawer with `focus: false, keyboard: false` (see fillModalElement) so its own
* focus enforcement and Escape handling stand aside and cannot fight the helper, which then owns
* moving focus in, the tab trap, Escape, and inerting the rest of the page (DGF-3991).
*
* @returns {void}
*/
function openQuickViewDialog() {
    accessibleDialog.open($(QUICK_VIEW_MODAL_SELECTOR), {
        initialFocus: ".modal-header .close",
        returnFocusTo: $quickViewTrigger,
        onClose: (dialog) => hideQuickViewPanel($(dialog), isForcedHide)
    });
}

/**
* Returns focus into the drawer after its body has been re-rendered for a colour switch.
*
* The swatch the customer activated is destroyed by the render, which drops focus - and with it the
* tab position - back to the document. Focus goes to the newly selected swatch, or to the close
* button for a product with no colour swatches (DGF-3991).
*
* @param {jQuery} $modal - the Quick View modal
* @returns {void}
*/
function restoreFocusAfterReRender($modal) {
    const $selectedSwatch = $modal.find(".color-value.selected").closest(".color-attribute");
    const $target = $selectedSwatch.length ? $selectedSwatch : $modal.find(".modal-header .close");

    $target.first().trigger("focus");
}

/**
 * Generates the modal window on the first call.
 *
 * `tabindex="-1"` makes the container programmatically focusable, which the dialog pattern needs as
 * a fallback focus target - and which Bootstrap silently relied on, its `_element.focus()` having
 * been a no-op without it. The close button holds nothing but an `aria-hidden` SVG, so it is given
 * an accessible name from the label the trigger carries (DGF-3991).
 *
 * @param {string} [closeLabel] - localised accessible name for the close button
 */
function getModalHtmlElement(closeLabel) {
    if ($(QUICK_VIEW_MODAL_SELECTOR).length !== 0) {
        // Closed rather than just removed: dropping the node while the helper still holds it open
        // would leave the rest of the page inert with nothing left to un-inert it (DGF-3991).
        closeQuickViewModal(true);
        $(QUICK_VIEW_MODAL_SELECTOR).remove();
    }
    var htmlString = "<!-- Modal -->"
        + "<div class=\"fade custom-modal mobile-quick-view-modal quick-view-modal\" id=\"quickViewModal\" tabindex=\"-1\" role=\"dialog\">"
        + "<div class=\"modal-dialog modal-dialog-centered quick-view-dialog\">"
        + "<!-- Modal content-->"
        + "<div class=\"modal-content\">"
        + "<div class=\"modal-header\">"
        + "    <button type=\"button\" class=\"close pull-right\">"
        + "        " + constants.ModalIcons.closeSvg
        + "    </button>"
        + "</div>"
        + "<div class=\"modal-body\"></div>"
        + "</div>"
        + "</div>"
        + "</div>";
    $("body").append(htmlString);

    // Set as an attribute rather than concatenated into the markup above, so a label containing a
    // quote cannot break out of it.
    if (closeLabel) {
        $(QUICK_VIEW_MODAL_SELECTOR).find(".modal-header .close").attr("aria-label", closeLabel);
    }
}

/**
 * Parse HTML code in Ajax response
 *
 * @param {string} html - Rendered HTML from quickview template
 * @return {QuickViewHtml} - QuickView content components
 */
function parseHtml(html) {
    var $html = $("<div>").append($.parseHTML(html));

    var body = $html.find(".product-quickview");
    var footer = $html.find(".modal-footer").children();

    return { body: body, footer: footer };
}

/**
 * replaces the content in the modal window on for the selected product variation.
 * @param {string} selectedValueUrl - url to be used to retrieve a new product model
 */
function fillModalElement(selectedValueUrl) {
    // A colour switch re-renders the body of a drawer that is already open, which destroys the
    // focused swatch; focus is put back once the render completes (DGF-3991).
    const isReRender = accessibleDialog.isOpen($(QUICK_VIEW_MODAL_SELECTOR));

    $.ajax({
        url: selectedValueUrl,
        method: "GET",
        dataType: "json",
        success: function (data) {
            var parsedHtml = parseHtml(data.renderedTemplate);
            const quickViewModalBody = $("#quickViewModal .modal-body");
            const qvModal = $("#quickViewModal");

            quickViewModalBody.html(parsedHtml.body);
            $(window).trigger("resize");

            base.methods.initProductImagesCarousel();
            $("body").trigger("quickview:ready");

            // focus and keyboard are left to the accessible dialog helper - see openQuickViewDialog.
            qvModal.modal({ focus: false, keyboard: false });

            const $renderUncachedDataWrapper = quickViewModalBody.find(".renderUncachedData-wrapper");
            if ($renderUncachedDataWrapper.length) {
                const remoteincludeurl = $renderUncachedDataWrapper.data("remoteincludeurl");
                $.ajax({
                    url: remoteincludeurl,
                    method: "GET",
                    success: function (renderedData) {
                        $renderUncachedDataWrapper.html(renderedData);
                        moveToActiveColor();
                        initColorsCarousel();

                        if (window.innerWidth > md) {
                            matchWidth($(".custom-product-size-button"));
                        }

                        $(".full-pdp-link").removeClass("d-none");
                        qvModal.spinner().stop();
                        base.handleOneSizeProductLowStockIndicator($renderUncachedDataWrapper);
                        qvModal.addClass("open");

                        if (isReRender) {
                            restoreFocusAfterReRender(qvModal);
                        }

                        // check Notify-me-form for invalid fields in quickView modal after quickView is rendered
                        invalid();
                    }
                });
            } else {
                qvModal.spinner().stop();
            }
        }
    });
}

module.exports = {
    showQuickview: function () {
        $("body").on("click", ".quickview, .mobile-quickview-button", function (e) {
            e.preventDefault();
            const $trigger = $(this);
            const selectedValueUrl = $trigger.attr("data-href");

            $(e.target).trigger("quickview:show");
            getModalHtmlElement($trigger.attr("data-close-label"));

            // Held for the close, when focus returns to the control the drawer was opened from. Set
            // after the shell is rebuilt, so a drawer being closed as part of that keeps the trigger
            // it was opened from.
            $quickViewTrigger = $trigger;

            fillModalElement(selectedValueUrl);
        });
    },
    updateQuickview: function () {
        $("body").on("click", ".product-quickview button.color-attribute", function (e) {
            e.preventDefault();

            if ($(this).children(".color-value").hasClass("selectable")) {
                $(".quick-view-dialog").spinner().start();
                var selectedValueUrl = $(this).attr("href");
                fillModalElement(selectedValueUrl);
            }
        });

        $(document).on("shown.bs.modal", "#quickViewModal", function () {
            setTimeout(function () {
                $(".product-thumbnails-list").trigger("resize");
            }, 0);

            if (window.innerWidth > md) {
                matchWidth($(".custom-product-size-button"));
            }
        });
    },
    /**
    * Gives the drawer the dialog semantics, focus handling and tab trap of the WAI-ARIA modal dialog
    * pattern, once Bootstrap has it on screen and its content can take focus.
    *
    * This replaces a hand-rolled trap that never ran: its first and last selectors
    * (".full-pdp-link", ".add-to-cart-global", ".modal-footer .quantity-select") named elements that
    * either do not exist in the Superdry templates at all, or - in the case of the PDP link - sit
    * last in the DOM rather than first (DGF-3991).
    */
    manageDialogAccessibility: function () {
        $(document).on("shown.bs.modal", "#quickViewModal", openQuickViewDialog);
    },
    availability: base.availability,
    addToCart: base.addToCart,
    showSpinner: function () {
        $("body").on("product:beforeAddToCart", function (e, data) {
            $(data).closest(".modal-content").spinner().start();
        });
    },
    hideDialog: function () {
        $("body").on("product:afterAddToCart", () => closeQuickViewModal());
        $("body").on("product:afterAddToWishlist", () => closeQuickViewModal());

        // Escape is handled by the accessible dialog helper, which closes only the topmost dialog.
        // The handler that used to live here fired on every Escape anywhere on the page, so closing
        // the nested size guide took the whole drawer down with it (DGF-3991).

        // With keyboard: false, Bootstrap also answers that Escape with its static-backdrop
        // "bounce", which ends by focusing the drawer's outer element - at the foot of the page -
        // and scrolls there, straight after the helper has returned focus to the tile. Cancelling
        // hidePrevented stops it before that focus (DGF-4872).
        $(document).on("hidePrevented.bs.modal", QUICK_VIEW_MODAL_SELECTOR, function (e) {
            e.preventDefault();
        });

        $(document).on("click", ".size-chart-modal", function (e) {
            e.stopPropagation();
        });

        // Close inner modal when its close button is clicked
        $(document).on("click", ".size-chart-close", function () {
            $(".size-chart-modal").modal("hide");
        });

        $(document).on("click", ".mobile-quick-view-modal .close, .btn-notify-me-modal-close, .modal-backdrop", () => closeQuickViewModal());
    },
    beforeUpdateAttribute: function () {
        $("body").on("product:beforeAttributeSelect", function () {
            $(".quick-view-dialog").spinner().start();
        });
    },
    updateAttribute: function () {
        $("body").on("product:afterAttributeSelect", function (e, response) {
            if ($(".modal.show .product-quickview>.bundle-items").length) {
                $(".modal.show").find(response.container).data("pid", response.data.product.id);
                $(".modal.show").find(response.container)
                    .find(".product-id").text(response.data.product.id);
            } else if ($(".set-items").length) {
                response.container.find(".product-id").text(response.data.product.id);
            } else {
                $(".modal.show .product-quickview").data("pid", response.data.product.id);
            }
        });
    },
    updateAddToCart: function () {
        $("body").on("product:updateAddToCart", function (e, response) {
            // update local add to cart (for sets)
            $("button.add-to-cart", response.$productContainer).attr("disabled",
                (!response.product.available));

            // update global add to cart (single products, bundles)
            var dialog = $(response.$productContainer)
                .closest(".quick-view-dialog");

            $(".add-to-cart-global", dialog).attr("disabled",
                !$(".global-availability", dialog).data("ready-to-order")
                || !$(".global-availability", dialog).data("available")
            );
        });
    },
    toggleQuickViewBtn: function () {
        // The quick view button overlays the image but sits outside the tile's link, so the image
        // and the button are treated as one hover area rather than as parent and child.
        const hoverArea = ".image-container, .quickview";
        const tileSelector = ".product-tile, .product-tile-pd";

        const toggleShowAnimation = (element, isHovered) => {
            const $tile = $(element).closest(tileSelector);
            $tile.find(".quickview").stop().toggleClass("show-animation", isHovered);
            $tile.find(".promo-labels").toggleClass("show-animation", isHovered);
        };

        $("body").on("mouseenter", hoverArea, function () {
            // Only one tile shows its quick view at a time, even if a mouseleave was missed
            $(".quickview.show-animation, .promo-labels.show-animation").removeClass("show-animation");
            toggleShowAnimation(this, true);
        });

        $("body").on("mouseleave", hoverArea, function (event) {
            const tile = $(this).closest(tileSelector)[0];
            const $movingTo = event.relatedTarget ? $(event.relatedTarget).closest(hoverArea) : $();

            // Moving between the image and the button of the same tile keeps it shown. Moving to a
            // different tile must not, or that tile would keep its quick view on display.
            if (tile && $movingTo.closest(tileSelector)[0] === tile) {
                return;
            }
            toggleShowAnimation(this, false);
        });
    },
    updateAvailability: function () {
        $("body").on("product:updateAvailability", function (e, response) {
            // bundle individual products
            $(".product-availability", response.$productContainer)
                .data("ready-to-order", response.product.readyToOrder)
                .data("available", response.product.available)
                .find(".availability-msg")
                .empty()
                .html(response.message);


            var dialog = $(response.$productContainer)
                .closest(".quick-view-dialog");

            if ($(".product-availability", dialog).length) {
                // bundle all products
                var allAvailable = $(".product-availability", dialog).toArray()
                    .every(function (item) { return $(item).data("available"); });

                var allReady = $(".product-availability", dialog).toArray()
                    .every(function (item) { return $(item).data("ready-to-order"); });

                $(".global-availability", dialog)
                    .data("ready-to-order", allReady)
                    .data("available", allAvailable);

                $(".global-availability .availability-msg", dialog).empty()
                    .html(allReady ? response.message : response.resources.info_selectforstock);
            } else {
                // single product
                $(".global-availability", dialog)
                    .data("ready-to-order", response.product.readyToOrder)
                    .data("available", response.product.available)
                    .find(".availability-msg")
                    .empty()
                    .html(response.message);
            }
        });
    },
    /**
    * Attaches a window resize event listener to update the product images carousel.
    * On each resize event, re-initializes the product images carousel to ensure proper display and responsiveness.
    */
    updateProductCarouselOnResize: function () {
        window.addEventListener("orientationchange", () => {
            const isQuickViewModalOpened = $(".quick-view-modal").hasClass("show");

            if (!isQuickViewModalOpened) return;

            closeQuickViewModal(true);

            const pid = $(".quick-view-dialog .product-quickview .add-to-cart").data("pid");
            const $currentlySelectedQuickViewBtn = window.innerWidth > md ?
                $(`.product-grid .quickview[data-pid="${pid}"]`) :
                $(`.product-grid .mobile-quickview-button[data-pid="${pid}"]`);

            if ($currentlySelectedQuickViewBtn.length) {
                $currentlySelectedQuickViewBtn.trigger("click");
            }
        });
    }
};
